/* ============================================================================
   FOND-IA — quitar el fondo con segmentacion por IA
   ----------------------------------------------------------------------------
   @imgly/background-removal corre 100% en el navegador (ONNX + WASM): sin
   backend, sin API key, sin que la foto del cliente salga de su telefono.

   Sustituye al filtro RemoveColor, que solo borraba pixeles parecidos al
   blanco: fallaba con cualquier fondo con textura y se comia las partes
   blancas del propio motivo.

   Dos costes que hay que gestionar:
     - la primera vez descarga el modelo (varios MB) -> precarga al abrir el
       panel Photos, para que la espera ocurra mientras el cliente mira
     - en un movil antiguo puede tardar mucho -> timeout y mensaje claro,
       nunca una interfaz colgada
   ========================================================================== */

const FondIA = (() => {

  const CDN = 'https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.5.5/+esm';

  /* MODELOS. La libreria acepta tres y por DEFECTO usa el mediano (isnet_fp16).
     Eso explica lo que veia Franklin en fotos reales de personas: « se borra la
     mayoria de cosas y no solo el fondo ». El grande (isnet, precision
     completa) acierta mucho mas en pelo, manos y bordes contra un fondo de
     casa o de calle.

       small  -> isnet_quint8  ~10 Mo, rapido, el peor
       medium -> isnet_fp16    ~20 Mo, el defecto de la libreria
       large  -> isnet         ~40 Mo, el mejor, y el que Franklin quiere

     Politica: en ESCRITORIO se usa el grande de entrada — hay potencia y el
     tiempo se acepta. En MOVIL se empieza por el mediano, porque un iPhone con
     el grande puede tardar mucho o quedarse sin memoria, y se ofrece el grande
     con un boton « Améliorer le détourage ». Asi el caso normal es rapido y el
     caso dificil tiene salida. */
  const esMobile = () => Math.min(innerWidth, innerHeight) < 820
                      || /iPhone|iPad|Android/i.test(navigator.userAgent);
  /* EN iPHONE, EL PEQUEÑO — Y NUNCA EL GRANDE.
     Franklin, en su iPhone: « puse quitar fondo, cargó hasta 25 % y luego me
     saltó y me volvió, y no quitó el fondo ». Safari había matado la pestaña
     (« Un problème récurrent est survenu »): el modelo ONNX reserva su memoria
     de golpe al cargarse, y un iPhone da poca a una pestaña que además tiene
     abierto el Studio con sus fotos. La foto ya se reducía a 1024 px antes;
     lo que no cabía era el modelo. En iPhone/iPad se empieza por el pequeño
     (≈10 Mo) y « Améliorer le détourage » sube como mucho al mediano: el
     grande (≈40 Mo) no se ofrece ahí, porque su « mejor » era cerrar la página. */
  const esIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent)
                   || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const MODELE_DEFAUT = () => (esIOS() ? 'small' : esMobile() ? 'medium' : 'large');
  const MODELE_MAX = 'large';
  const modeleSur = m => (esIOS() && m === 'large') ? 'medium' : m;
  /* TOPE POR INACTIVIDAD, NO POR DURACION.
     Medido: la descarga en frio del modelo grande paso de 230 s en esta
     conexion. Un tope total de 75 s mataba un trabajo que iba PERFECTAMENTE
     —la barra avanzaba— solo por tardar. Y peor: matarlo no lo detiene, porque
     una promesa no se puede cancelar; simplemente se deja de escuchar mientras
     el trabajo sigue vivo por detras.

     Lo que de verdad hay que detectar no es « tarda mucho » sino « no pasa
     nada ». Mientras la libreria informe de progreso, el trabajo esta vivo y
     se le deja seguir. Si se calla mas de INACTIVITE_MAX, ahi si algo se ha
     roto. La inferencia no informa, asi que su silencio se cubre con un
     margen amplio: mide unos 10 s, se le dan 90.

     El requisito de verdad no era el tope: era poder cancelar, y cancelar
     funciona en cualquier momento. */
  const INACTIVITE_MAX = 90000;
  const DUREE_ABSOLUE  = 10 * 60 * 1000;   // red de seguridad, no un plazo

  let _module = null;      // promesa del import
  let _prete = false;
  let _echec = null;

  /* Carga el modulo (no el modelo). Idempotente. */
  function charger() {
    if (_module) return _module;
    _poser('chargement', 0);
    _module = import(/* webpackIgnore: true */ CDN)
      .then(m => { _prete = true; return m; })
      .catch(e => { _echec = e; _module = null; _poser('erreur'); throw e; });
    return _module;
  }

  /* Precarga en segundo plano al abrir el panel Photos. La libreria expone su
     propio preload(), que baja el modelo ONNX antes de que haga falta: asi la
     espera ocurre mientras el cliente elige la foto, no despues.
     Nunca lanza: si falla, ya lo dira el intento real. */
  let _modelePrecharge = null;
  function precharger(modele) {
    const mod = modeleSur(modele || MODELE_DEFAUT());
    charger().then(m => {
      if (_modelePrecharge === mod || typeof m.preload !== 'function') { _poser('pret', 100); return; }
      _modelePrecharge = mod;
      /* Se precarga EL MISMO modelo que se va a usar: precargar el mediano y
         despues pedir el grande seria bajar dos veces. */
      return m.preload({
        model: mod,
        progress: (cle, actuel, total) => {
          if (total) _poser('chargement', Math.round(actuel / total * 100));
        },
      }).then(() => _poser('pret', 100))
        .catch(e => { _modelePrecharge = null; _poser('erreur'); console.warn('[EDISAMP][IA] preload', e); });
    }).catch(() => {});
  }

  /* Estado observable, para que el boton nunca quede mudo:
       'repos'      no se ha pedido nada todavia
       'chargement' bajando modulo o modelo
       'pret'       listo, la proxima pasada es solo inferencia
       'erreur'     sin red, CDN bloqueado, dispositivo incapaz            */
  let _etat = 'repos', _pc = 0;
  const _abonnes = [];

  function _poser(etat, pc) {
    _etat = etat;
    if (pc !== undefined) _pc = pc;
    console.info('[EDISAMP][IA]', etat, pc !== undefined ? pc + '%' : '');
    _abonnes.forEach(f => { try { f(_etat, _pc); } catch (e) {} });
  }

  const surEtat = f => { _abonnes.push(f); f(_etat, _pc); };
  const etat = () => _etat;
  const pourcentage = () => _pc;
  const disponible = () => _prete;
  const erreurChargement = () => _echec;

  /**
   * Rechaza si el trabajo se QUEDA CALLADO, no si tarda.
   * `latido()` lo devuelve para que quien reciba progreso pueda decir « sigo ».
   */
  function avecInactivite(fabrica) {
    let ultimo = Date.now();
    const latido = () => { ultimo = Date.now(); };
    const debut = Date.now();
    return new Promise((res, rej) => {
      let vivo = true;
      const reloj = setInterval(() => {
        if (!vivo) return;
        if (Date.now() - ultimo > INACTIVITE_MAX) {
          vivo = false; clearInterval(reloj);
          rej(new Error('inactivite'));
        } else if (Date.now() - debut > DUREE_ABSOLUE) {
          vivo = false; clearInterval(reloj);
          rej(new Error('timeout'));
        }
      }, 1000);
      fabrica(latido).then(
        v => { if (vivo) { vivo = false; clearInterval(reloj); res(v); } },
        e => { if (vivo) { vivo = false; clearInterval(reloj); rej(e); } });
    });
  }

  /* dataUrl -> dataUrl PNG con fondo transparente.
     Lanza si falla o si tarda mas de DELAI_MAX. */
  async function supprimerFond(dataUrl, surProgres, modele) {
    const m = await charger();
    const fn = m.removeBackground || m.default;
    if (typeof fn !== 'function') throw new Error('API inattendue');
    const mod = modeleSur(modele || MODELE_DEFAUT());

    const blob = await avecInactivite(latido => fn(dataUrl, {
      model: mod,
      output: { format: 'image/png', quality: 0.9 },
      progress: (cle, actuel, total) => {
        latido();                       // hay senal de vida: no se corta
        if (total) {
          const pc = Math.round(actuel / total * 100);
          console.info('[EDISAMP][IA]', cle, pc + '%');
          if (surProgres) surProgres(pc, cle);
        }
      },
    }));
    _poser('pret', 100);

    return await new Promise((res, rej) => {
      const l = new FileReader();
      l.onload = () => res(l.result);
      l.onerror = rej;
      l.readAsDataURL(blob);
    });
  }

  return { precharger, supprimerFond, disponible, erreurChargement,
           etat, pourcentage, surEtat,
           MODELE_DEFAUT, MODELE_MAX, esMobile };
})();
